import PublicLayout from "@/components/PublicLayout";
import { Link } from "react-router-dom";
import { CONTACT, waLink } from "@/lib/config";
import { MessageCircle, Phone, ShieldCheck, HandCoins, Handshake, Sparkles, CheckCircle2 } from "lucide-react";

const PILLARS = [
  { i: HandCoins, t: "No hidden charges", d: "You'll always know the fixed fee before we begin. No surprise bills." },
  { i: Handshake, t: "Priced to your case", d: "Same-service scope varies with turnover, volume, complexity and urgency — so we quote per case." },
  { i: ShieldCheck, t: "Senior-CA reviewed", d: "A qualified Chartered Accountant reviews every quote — you're not being sold a template." },
  { i: Sparkles, t: "Package or retainer", d: "One-time filings or long-term retainer models — we shape it to what suits you." },
];

const TYPES = [
  { name: "One-time services", eg: "Company/LLP registration, GST registration, IEC, DSC, Trademark, Notice reply" },
  { name: "Recurring compliance", eg: "GST/TDS/ITR filing, Accounting, Payroll, ROC — monthly / quarterly retainer" },
  { name: "Advisory retainers", eg: "Virtual CFO, Business consultancy, Startup consultancy — monthly retainer" },
];

export default function Pricing() {
  const wa = waLink("Hi KK Associates, I'd like a custom quote. Here are my details: business type, turnover, and service required.");

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-8">
        <div className="text-xs uppercase tracking-widest text-blue-700">Pricing</div>
        <h1 className="h-display mt-3 text-5xl md:text-6xl text-slate-900 max-w-4xl">Fair pricing, tailored to your case.</h1>
        <p className="mt-5 max-w-2xl text-slate-600 leading-relaxed">
          The same service can cost differently for two clients — because turnover, volume of transactions,
          urgency and complexity are never identical. That's why we quote each engagement individually and
          keep it fixed once agreed.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href={wa} target="_blank" rel="noreferrer" data-testid="pricing-wa" className="btn-hover rounded-full bg-emerald-500 text-white px-6 py-3 text-sm font-medium inline-flex items-center gap-2 hover:bg-emerald-600">
            <MessageCircle size={16} /> Ask for Price on WhatsApp
          </a>
          <a href={`tel:${CONTACT.phoneRaw}`} data-testid="pricing-call" className="btn-hover rounded-full border border-slate-300 text-slate-900 px-6 py-3 text-sm font-medium inline-flex items-center gap-2 hover:bg-slate-100">
            <Phone size={16} /> Call {CONTACT.phone}
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {PILLARS.map((p, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-blue-300 hover:shadow-md transition-shadow duration-300">
            <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center mb-4"><p.i size={18} /></div>
            <div className="text-base font-medium text-slate-900">{p.t}</div>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.d}</p>
          </div>
        ))}
      </section>

      <section className="bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <h2 className="h-display text-3xl md:text-4xl text-slate-900">How we structure engagements</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {TYPES.map((t, i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-200 p-6">
                <div className="text-lg font-medium text-slate-900">{t.name}</div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{t.eg}</p>
                <div className="mt-6 text-sm text-blue-700 font-medium">Ask for a fixed quote →</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="rounded-3xl bg-slate-950 text-white p-10 md:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h3 className="h-display text-3xl md:text-4xl">Tell us what you need. We'll send a fixed fee.</h3>
            <p className="mt-3 text-slate-300 max-w-xl">Share your business type, turnover and the service you're exploring on WhatsApp — you'll usually have a quote within the same business day.</p>
            <ul className="mt-6 space-y-2 text-slate-300 text-sm">
              {[
                "No obligation, no automatic charge",
                "Fixed fee written down before we start",
                "Clarity on scope, timeline and deliverables",
              ].map((x, i) => <li key={i} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-blue-300" /> {x}</li>)}
            </ul>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <a href={wa} target="_blank" rel="noreferrer" data-testid="pricing-cta-wa" className="btn-hover rounded-full bg-white text-slate-900 px-6 py-3 text-sm font-medium hover:bg-blue-300 inline-flex items-center gap-2">
              <MessageCircle size={16} /> WhatsApp Us
            </a>
            <Link to="/contact" data-testid="pricing-cta-contact" className="btn-hover rounded-full border border-white/25 text-white px-6 py-3 text-sm font-medium hover:bg-white/10 inline-flex items-center gap-2">
              Send Enquiry
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
